import SideDrawer from './side-drawer.js';
import { debounce } from '../../utils/utils.js';
import { initLazyImages } from '../../utils/lazy-images.js';

/**
 * Configuration object for PredictiveSearch selectors and constants
 */
const SEARCH_CONFIG = {
    SELECTORS: {
        INPUT: 'input[type="search"]',
        RESULTS: '[data-search-results]',
        FORM: 'form',
        RESULT_LINK: 'a[href]',
        SECTION: '#shopify-section-predictive-search'
    },
    CLASSES: {
        LOADING: 'is-loading'
    },
    DEBOUNCE_DELAY: 300,
    MIN_QUERY_LENGTH: 2,
    FOCUS_DELAY: 100,
    CLOSE_DELAY: 100
};

class PredictiveSearch extends SideDrawer {
    constructor() {
        super();
    }

    connectedCallback() {
        super.connectedCallback();
        this.setupSearchElements();
        this.setupSearchListeners();
    }

    /**
     * Setup search-specific DOM elements
     */
    setupSearchElements() {
        this.searchInput = this.querySelector(SEARCH_CONFIG.SELECTORS.INPUT);
        this.searchResults = this.querySelector(SEARCH_CONFIG.SELECTORS.RESULTS);
        this.searchForm = this.querySelector(SEARCH_CONFIG.SELECTORS.FORM);
    }

    /**
     * Setup search-specific event listeners
     */
    setupSearchListeners() {
        if (!this.searchInput || !this.searchResults) return;

        // Handle search input with debounce
        this.searchInput.addEventListener(
            'input',
            debounce(this.handleSearchInput.bind(this), SEARCH_CONFIG.DEBOUNCE_DELAY)
        );

        // Handle form submission
        this.searchForm?.addEventListener('submit', this.handleFormSubmit.bind(this));

        // Handle result clicks
        this.addEventListener('click', this.handleResultClick.bind(this));

        // Clear results when closing and focus input when opening
        this.addEventListener('toggle', (evt) => {
            if (evt.detail.isOpen) {
                // Focus input when opening
                setTimeout(() => this.searchInput.focus(), SEARCH_CONFIG.FOCUS_DELAY);
            } else {
                this.clearResults();
                this.searchInput.value = '';
            }
        });
    }

    async handleSearchInput(evt) {
        const query = evt.target.value.trim();

        if (!query || query.length < SEARCH_CONFIG.MIN_QUERY_LENGTH) {
            this.clearResults();
            return;
        }

        await this.performSearch(query);
    }

    async performSearch(query) {
        try {
            // Show loading state
            this.searchResults.classList.add(SEARCH_CONFIG.CLASSES.LOADING);
            this.searchResults.innerHTML = '<div class="p-4 text-center text-gray-600">Searching...</div>';

            // Fetch predictive search results
            const response = await fetch(
                `${theme.routes.predictiveSearch}?q=${encodeURIComponent(query)}&section_id=predictive-search`
            );

            if (!response.ok) {
                throw new Error(`Search failed: ${response.status}`);
            }

            const text = await response.text();

            // Extract the HTML from the response
            const resultsMarkup = new DOMParser()
                .parseFromString(text, 'text/html')
                .querySelector(SEARCH_CONFIG.SELECTORS.SECTION)?.innerHTML;

            if (resultsMarkup) {
                this.searchResults.innerHTML = resultsMarkup;
                initLazyImages();
            } else {
                this.showNoResults(query);
            }
        } catch (error) {
            console.error('Search error:', error);
            this.showError();
        } finally {
            this.searchResults.classList.remove(SEARCH_CONFIG.CLASSES.LOADING);
        }
    }

    clearResults() {
        this.searchResults.innerHTML = '';
    }

    showNoResults(query) {
        this.searchResults.innerHTML = `
            <div class="p-6 text-center text-gray-600">
                <p class="mb-2">No results found for "${query}"</p>
                <p class="text-sm">Try adjusting your search terms</p>
            </div>
        `;
    }

    showError() {
        this.searchResults.innerHTML = `
            <div class="p-6 text-center text-red-600">
                <p>Sorry, something went wrong with your search.</p>
                <p class="text-sm mt-2">Please try again.</p>
            </div>
        `;
    }

    handleFormSubmit(evt) {
        // Allow form to submit if there's a query
        const query = this.searchInput.value.trim();
        if (!query) {
            evt.preventDefault();
        }
    }

    handleResultClick(evt) {
        // Close drawer when clicking on a result link
        const link = evt.target.closest(SEARCH_CONFIG.SELECTORS.RESULT_LINK);
        if (link && this.searchResults.contains(link)) {
            // Small delay to allow navigation
            setTimeout(() => this.close(), SEARCH_CONFIG.CLOSE_DELAY);
        }
    }
}

customElements.define('predictive-search', PredictiveSearch);
